import React, { FC, useMemo, useEffect } from 'react';
import { useParams, Outlet, useLocation, useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import { Spin } from 'antd';
import { LoadingOutlined } from '@ant-design/icons';
import { useGetTrainerClientById } from 'src/api/mutations/useGetTrainerClients';
import { auth } from 'src/config/firebase';
import { sessionStore } from 'src/store/sessionStore';
import { ReactComponent as OverviewHeaderCalendarIcon } from 'src/assets/images/trainer/clients/overview-header-calendar.svg';
import { ReactComponent as OverviewHeaderEmailIcon } from 'src/assets/images/trainer/clients/overview-header-email.svg';
import { ReactComponent as OverviewHeaderSendMessageIcon } from 'src/assets/images/trainer/clients/overview-header-send-message.svg';
import { EmptyState } from './components/EmptyState';

import './ClientOverview.scss';

const formatMemberSince = (createdAt: any) => {
  if (!createdAt) return '-';

  if (
    typeof createdAt === 'number' ||
    (typeof createdAt === 'string' && /^\d+$/.test(createdAt.trim()))
  ) {
    const num = Number(createdAt);
    const d = dayjs(num < 1e11 ? num * 1000 : num);
    if (d.isValid()) return d.format('MMMM DD, YYYY');
  }

  if (typeof createdAt === 'object') {
    const secs = createdAt.seconds ?? createdAt._seconds;
    if (typeof secs === 'number') {
      const d = dayjs(secs * 1000);
      if (d.isValid()) return d.format('MMMM DD, YYYY');
    }
  }

  const d = dayjs(createdAt);
  if (d.isValid()) {
    return d.format('MMMM DD, YYYY');
  }

  return '-';
};

export const ClientOverview: FC = () => {
  const { uid } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const currentUser = sessionStore((state) => state.currentUser);
  const trainerUid = auth.currentUser?.uid || currentUser?.uid || '';

  const { data, isLoading } = useGetTrainerClientById(uid);
  const client: any = useMemo(() => {
    return data?.clients?.[0] || null;
  }, [data?.clients]);

  const handleSendMessage = () => {
    const clientUid = client?.uid || client?.id || uid;
    if (!clientUid || !trainerUid) return;
    const chatId = [trainerUid, clientUid].sort().join('_');
    navigate(`/onboarding-trainer/dashboard/messages/${chatId}`);
  };

  useEffect(() => {
    const mainContainer = document.querySelector('.dashboard-main');
    if (mainContainer) {
      mainContainer.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [uid]);

  const tabs = ['Overview', 'Program', 'Workouts', 'Client Profile'];

  if (isLoading) {
    return (
      <div className="client-overview-container">
        <div className="loading-container">
          <Spin indicator={<LoadingOutlined className="loading-icon" spin />} />
          <span className="loading-text">Loading client details</span>
        </div>
      </div>
    );
  }

  if (!client) {
    return (
      <div className="client-overview-container" style={{ padding: '40px 24px' }}>
        <EmptyState
          message="The requested client details could not be found or have been removed."
          marginTop="0"
        />
      </div>
    );
  }

  return (
    <div className="client-overview-container">
      <div className="client-overview-container-wrapper">
        <div className="overview-header-card">
          <div className="client-profile-info">
            <div className="avatar">
              {client?.avatar_url || client?.avatarUrl ? (
                <img src={client.avatar_url || client.avatarUrl} alt="Profile" />
              ) : (
                <div className="avatar-placeholder">
                  {client?.name?.[0]}
                  {client?.surname?.[0]}
                </div>
              )}
            </div>
            <div className="details">
              <h2>
                {client?.name} {client?.surname}
              </h2>
              <div className="meta">
                <span className="email">
                  {<OverviewHeaderEmailIcon />} <span className="email-text">{client?.email}</span>
                </span>
                <span className="member-since">
                  {<OverviewHeaderCalendarIcon />} Member since:{' '}
                  {formatMemberSince(client?.createdAt || client?.created_at)}
                </span>
              </div>
            </div>
          </div>
          <button className="send-message-btn" onClick={handleSendMessage}>
            Send Message <OverviewHeaderSendMessageIcon />
          </button>
        </div>

        <div className="overview-tabs">
          {tabs.map((tab, index) => {
            const tabPath = tab.toLowerCase().replace(' ', '-');
            const isActive = location.pathname.includes(`/${tabPath}`);
            // Special handling for workouts to keep the tab active on WorkoutDetails
            const isWorkoutsActive = tab === 'Workouts' && location.pathname.includes('/workouts');

            return (
              <React.Fragment key={tab}>
                <button
                  className={`tab-btn ${isActive || isWorkoutsActive ? 'active' : ''}`}
                  onClick={() => navigate(tabPath)}
                >
                  {tab}
                </button>
                {index < tabs.length - 1 && <div className="tab-separator"></div>}
              </React.Fragment>
            );
          })}
        </div>
      </div>

      <div className="tab-outlet-area">
        <Outlet context={{ client }} />
      </div>
    </div>
  );
};
